/**
 * Telefone com DDI — espelha drderma-service/src/utils/phone.ts.
 *
 * Convenção de armazenamento:
 *   - `telephone_ddi` guarda só os dígitos do código do país ('55', '351', '1', ...).
 *   - `telephone` guarda o número nacional quando o DDI é 55 (máscara legada "(11) 91234-5678")
 *     e "+<ddi><número>" quando o DDI é estrangeiro.
 *
 * O "+" marca "este valor já contém DDI", e é o que impede o backend de reprefixar 55
 * em número estrangeiro no envio de WhatsApp.
 */

import { DEFAULT_DDI } from 'constants/phoneCountries';

const E164_MAX_DIGITS = 15;

export { DEFAULT_DDI };

export const onlyDigits = (value) => String(value ?? '').replace(/\D/g, '');

export const normalizeDdi = (value) => {
  const digits = onlyDigits(value);
  if (!digits || digits.length > 4) return DEFAULT_DDI;
  return digits.replace(/^0+/, '') || DEFAULT_DDI;
};

export const isBrazilDdi = (ddi) => normalizeDdi(ddi) === DEFAULT_DDI;

const hasExplicitDdi = (value) => String(value ?? '').trim().startsWith('+');

/** Monta o valor enviado ao banco a partir do DDI selecionado e do número digitado. */
export const buildStoredPhone = (ddi, phone) => {
  const raw = String(phone ?? '').trim();
  if (!raw) return '';

  const normalizedDdi = normalizeDdi(ddi);
  if (isBrazilDdi(normalizedDdi)) {
    if (hasExplicitDdi(raw)) {
      const digits = onlyDigits(raw);
      return digits.startsWith(DEFAULT_DDI) ? digits.slice(2) : digits;
    }
    return raw;
  }

  let digits = onlyDigits(raw);
  if (digits.startsWith(normalizedDdi)) digits = digits.slice(normalizedDdi.length);
  if (!digits) return '';
  return `+${normalizedDdi}${digits}`;
};

/** Separa o valor vindo do banco em { ddi, national } para preencher o formulário. */
export const splitStoredPhone = (stored, ddiColumn) => {
  const raw = String(stored ?? '').trim();
  const ddi = normalizeDdi(ddiColumn);

  if (!raw) return { ddi, national: '' };

  if (hasExplicitDdi(raw)) {
    const digits = onlyDigits(raw);
    return { ddi, national: digits.startsWith(ddi) ? digits.slice(ddi.length) : digits };
  }

  return { ddi, national: raw };
};

/**
 * E.164 em dígitos (sem "+"), espelhando `toWhatsappDigits` do backend.
 * Aceita tanto o valor armazenado quanto um número solto — nesse caso usa `ddiColumn`.
 */
export const toE164Digits = (stored, ddiColumn) => {
  const raw = String(stored ?? '').trim();
  const digits = onlyDigits(raw);
  if (!digits) return '';

  if (hasExplicitDdi(raw)) return digits;

  const ddi = normalizeDdi(ddiColumn);
  if (isBrazilDdi(ddi)) {
    // Nacional brasileiro tem no máximo 11 dígitos; mais que isso já inclui DDI.
    return digits.length > 11 ? digits : `${DEFAULT_DDI}${digits}`;
  }
  return digits.startsWith(ddi) ? digits : `${ddi}${digits}`;
};

/** Regras de tamanho: Brasil é estrito (10 ou 11); o resto segue o limite do E.164. */
export const isValidNationalNumber = (ddi, phone) => {
  const normalizedDdi = normalizeDdi(ddi);
  const digits = onlyDigits(splitStoredPhone(phone, normalizedDdi).national);

  if (isBrazilDdi(normalizedDdi)) return digits.length === 10 || digits.length === 11;
  return digits.length >= 4 && digits.length <= E164_MAX_DIGITS - normalizedDdi.length;
};

export const phoneValidationMessage = (ddi) =>
  isBrazilDdi(ddi)
    ? 'Informe o telefone com DDD (10 ou 11 dígitos)'
    : 'Informe o telefone com o código de área do país';

/**
 * Exibição: brasileiro mantém a máscara; estrangeiro mostra "+DDI número".
 * `ddiColumn` é opcional — sem ela um valor em E.164 é exibido sem separar o DDI,
 * em vez de assumir Brasil e produzir "+55 351...".
 */
export const formatPhoneForDisplay = (stored, ddiColumn) => {
  const raw = String(stored ?? '').trim();
  if (!raw) return '';

  const digits = onlyDigits(raw);
  if (!digits) return '';

  if (hasExplicitDdi(raw)) {
    if (!onlyDigits(ddiColumn)) return `+${digits}`;
    const { ddi, national } = splitStoredPhone(raw, ddiColumn);
    return national ? `+${ddi} ${national}` : `+${digits}`;
  }

  const ddi = normalizeDdi(ddiColumn);
  return isBrazilDdi(ddi) ? raw : `+${ddi} ${digits}`;
};
